import React from "react";
import { useNavigate } from "react-router-dom";
import { rupee, imgSrc, placeholderFor } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { track } from "@/lib/track";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";

export default function ProductCard({ product, store }) {
  const nav = useNavigate();
  const { qtyOf, addItem, inc, dec } = useCart();
  const qty = qtyOf(product.id);
  const disc = product.discount_pct || 0;

  const goDetail = () => nav(`/product/${product.id}`);

  const handleAdd = (e) => {
    e.stopPropagation();
    const st = store || product.store || { id: product.store_id };
    if (!st || !st.id) return goDetail();
    const res = addItem(product, st);
    if (!res.conflict) {
      track("cart_add", { product_id: product.id, store_id: st.id, category: product.category });
      toast.success(`${product.name} added to cart`);
    }
  };
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <div data-testid="product-card-item" onClick={goDetail}
      className="group relative flex cursor-pointer flex-col justify-between rounded-2xl border border-slate-200/80 bg-white p-3 transition-all duration-200 hover:border-brand/40 hover:shadow-md">
      {disc > 0 && (
        <span data-testid="product-discount-badge" className="absolute left-2 top-2 z-10 rounded-md bg-amber-surge px-1.5 py-0.5 text-[10px] font-bold text-white">
          {disc}% OFF
        </span>
      )}
      <div className="overflow-hidden rounded-xl bg-slate-50">
        <img src={imgSrc(product.image, product.category)} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placeholderFor(product.category); }} alt={product.name} loading="lazy"
          className="h-28 w-full object-cover transition-transform duration-300 group-hover:scale-105" />
      </div>
      <div className="mt-2 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{product.unit}</p>
        <h4 className="mt-0.5 line-clamp-2 text-sm font-semibold leading-tight text-slate-800 group-hover:text-brand">{product.name}</h4>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <div>
          <span data-testid="product-price-display" className="font-mono text-sm font-bold text-slate-900">{rupee(product.price)}</span>
          {disc > 0 && <span data-testid="product-mrp-display" className="ml-1 font-mono text-xs text-slate-400 line-through">{rupee(product.mrp)}</span>}
        </div>
        {qty === 0 ? (
          <button data-testid="product-add-to-cart-btn" onClick={handleAdd}
            className="rounded-lg border border-brand/30 bg-brand-soft px-3 py-1.5 text-xs font-bold text-brand transition-all duration-100 hover:bg-brand hover:text-white active:scale-90">
            ADD
          </button>
        ) : (
          <div className="flex items-center gap-2 rounded-lg bg-brand px-1.5 py-1 text-white" onClick={(e) => e.stopPropagation()}>
            <button data-testid="product-decrement-qty-btn" onClick={stop(() => dec(product.id))} className="active:scale-90"><Minus className="h-4 w-4" /></button>
            <span data-testid="product-qty-badge" className="min-w-[18px] text-center text-sm font-bold">{qty}</span>
            <button data-testid="product-increment-qty-btn" onClick={stop(() => inc(product.id))} className="active:scale-90"><Plus className="h-4 w-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
}
